import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ReportService } from '../_services/report.service';
import { ReportDataService } from './report-data.service';

/** The filter lists a client choice loads on the Reports pages. */
export type ReportFilterLookup = 'coordinators' | 'students' | 'tutors' | 'referrers';

/**
 * Loads the Reports pages' filter lists for the chosen client into ReportDataService. A lookup that fails
 * (after ReportService's one retry) leaves its list empty and is recorded in failed, so the page shows an
 * inline "Couldn't load" note with a Retry link rather than an error popup. An empty result is a normal,
 * empty list (40760).
 */
@Injectable({ providedIn: 'root' })
export class ReportFilterLookupsService {

  /** The lookups whose last load failed. */
  public readonly failed = new Set<ReportFilterLookup>();

  private params: { clientIds: any[] } = null;

  constructor(private reportService: ReportService, private reportDataService: ReportDataService) { }

  /** Loads every filter list for the client. */
  public load(clientId: any): void {
    this.params = { clientIds: [clientId] };
    this.failed.clear();
    (['coordinators', 'students', 'tutors', 'referrers'] as ReportFilterLookup[]).forEach(lookup => this.fetch(lookup));
  }

  /** Loads one list again for the last client chosen. */
  public retry(lookup: ReportFilterLookup): void {
    if (this.params != null) {
      this.fetch(lookup);
    }
  }

  private fetch(lookup: ReportFilterLookup): void {
    this.failed.delete(lookup);
    const params = this.params;
    this.request(lookup, params).subscribe({
      next: (data: any[]) => {
        if (params === this.params) {
          this.setList(lookup, data ?? []);
        }
      },
      error: () => {
        if (params === this.params) {
          this.setList(lookup, []);
          this.failed.add(lookup);
        }
      }
    });
  }

  private request(lookup: ReportFilterLookup, params: any): Observable<any> {
    switch (lookup) {
      case 'coordinators': return this.reportService.GetCoordinatorsForReport(params);
      case 'students': return this.reportService.GetStudentsForReport(params);
      case 'tutors': return this.reportService.GetTutorsForReport(params);
      case 'referrers': return this.reportService.GetReferrersForReport(params);
    }
  }

  private setList(lookup: ReportFilterLookup, data: any[]): void {
    switch (lookup) {
      case 'coordinators': this.reportDataService.coordinatorList = data; break;
      case 'students': this.reportDataService.studentList = data; break;
      case 'tutors': this.reportDataService.tutorList = data; break;
      case 'referrers': this.reportDataService.referrerList = data; break;
    }
  }
}
